/**
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2018, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define([
	'underscore',
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'./api/ViprUITestApi',
	'./BinarySearchTupleLocator',
	'./SelectorTupleLocator'
], function (_, APIFactory, ViprUITestAPI, BinarySearchTupleLocator, SelectorTupleLocator) {


	/**
	 * The ViprUITest is a support class for the functional Test UI.
	 * Its purpose is to allow the test framework to check and interact with a visualization from an external point of view.
	 * In the case of VIPR, we assume that VIPR "does the right thing" and checking its structures via getItemsAtPoint/getItemsInPolygon is sufficient.
	 * The hope is to have VIPR implement an interface and this class will simply adapt to the UITestAPI interface and not use private information.
	 */
	class ViprUITest {
		constructor(options) {
			this.content = options.content;
		}

		getAPI() {
			if (!this._api) {
				this._api = APIFactory.createAPI(this, [ViprUITestAPI]);
			}
			return this._api;
		}

		_initLocators() {
			if (!this.locators) {
				const viprView = this.content.getFeature('livewidget.internal').getVisView();
				const viprWidget = viprView.viprWidget;
				const viprViewElement = viprView.$el;
				this.locators = {
					data: new BinarySearchTupleLocator({ viprWidget }),	//Most test logic will want this one
					axis: new SelectorTupleLocator({ '$el': viprViewElement, viprWidget, selector: '.axis-label' }),	//Most cases won't need selectors like these
					legend: new SelectorTupleLocator({ '$el': viprViewElement, viprWidget, selector: '.Legend li' })	//however these features only work with selectors (at the moment)
				};
			}
		}

		/**
		 * given a searchTuple of captions, return points in the visualization that match.
		 * @param searchTuple - an array of captions to match eg: ['2004', 'Friday'] for dataPoint which is the intersection of Year and Release Day.
		 * @param [options]
		 * @param [options.axis]   - return axis matches (eg: for bar charts)
		 * @param [options.legend] - return legend matches
		 * @param [options.data]   - return data point matches
		 * @returns an object with the match (or null).
		 */
		findPoint(searchTuple, options) {
			this._initLocators();
			if(!options) {
				options = {
					axis: true,
					legend: true,
					data: true
				};
			}

			const locatorKeys = Object.keys(this.locators);

			for(let i = 0; i < locatorKeys.length; i++) {
				const locatorKey = locatorKeys[i];
				if(options[locatorKey]) {
					const locator = this.locators[locatorKey];
					const results = locator.findTupleVisualInfo({ tuple:searchTuple }, { includePoints:true });
					if(results.length) {
						return results[0].point;
					}
				}
			}
		}

		/**
		 * Take a 'snapshot' of the visualization which includes the tuples which are part of the visualization
		 * and their selection state.
		 * @param filter - (optional) - if specified, only the itemInfo which matches the filter are returned. eg: { isSelected: true }
		 */
		takeSnapshot(filter) {
			this._initLocators();
			const results={};

			Object.keys(this.locators).forEach(locatorKey =>
				results[`${locatorKey}Points`] = this.locators[locatorKey]
					.findTupleVisualInfo(filter, { includeInfo: true })
					.map(({ info }) => info)
			);

			return results;
		}
	}

	return ViprUITest;
});
